// src/views/MarketView.js — extracted from App.js
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { compressImage, cropPhotoFromId, fileToBase64, liveOffers, acceptedOffers, isOfferLive, validateGovId, extractIdDetails } from "../lib/utils";
import { SK, ADMIN_EMAIL, SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "../constants";
import { SellerCard } from "../components/common";
import { UPIModal } from "../components/UPIModal";
import { MakeOfferModal, AddressConfirmModal } from "../components/MakeOfferModal";
import { useLanguage, t } from "../lib/i18n";

// ─── MARKET VIEW ─────────────────────────────────────────────────────────────
export function MarketView({ sellers, listings, saveSellers, saveListings, requests, saveRequests, users, saveUsers, session, refreshSession, openAuth, requireProfile, toast, setView, initialTab="browse", onTabChange, addNotif, inviteParams=null }) {
  const { lang } = useLanguage();
  const [tab,setTab]         = useState(initialTab);
  const switchTab = (s) => { setTab(s); onTabChange?.(s); };
  useEffect(() => { if (initialTab) { setTab(initialTab); } }, [initialTab]);
  const [filt,setFilt]       = useState("all");
  const [selSel,setSelSel]   = useState(null);
  const [lf,setLF]           = useState({brand:"",model:"",reference:"",decade:"",condition:"",price:"",description:"",tags:""});
  const [sf,setSF]           = useState({name:session?.user?.name||"",handle:"",city:session?.user?.addressCity||"",bio:"",email:session?.user?.email||"",instagram:"",whatsapp:session?.user?.phone||""});
  const [sfErrors,setSFErrs] = useState({});
  const [appDone,setAD]      = useState(false);
  const [idFile,setIdFile]     = useState(null);
  const [idChecking,setIdChk]  = useState(false);
  const [idValid,setIdValid]   = useState(false);
  const [idUploading,setIdu]   = useState(false);
  const [enquiryListing,setEL] = useState(null);
  const [enquiryMsg,setEM]   = useState("");
  const [enquiryDone,setED]  = useState(null);
  const lu=(k,v)=>setLF(p=>({...p,[k]:v}));
  const su=(k,v)=>setSF(p=>({...p,[k]:v}));
  const [detailListing, setDL] = useState(null);
  const [galleryIdx, setGI] = useState(0);
  const openDetail = (listing) => { setDL(listing); setGI(0); };

  const getBySeller = id => sellers.find(s=>s.id===id);
  const shown = listings.filter(l=>filt==="all"||getBySeller(l.sellerId)?.verified);
  const mySellerId = session?.user?.sellerId;
  const mySeller = sellers.find(s=>s.id===mySellerId) || sellers.find(s=>s.email===session?.user?.email);
  const isSeller = (session?.user?.role==="seller")||(session?.user?.roles||[]).includes("seller");
  const isAdmin  = (session?.user?.role==="admin")||(session?.user?.roles||[]).includes("admin");

  // Pre-fill apply form from existing seller profile when resubmitting
  useEffect(()=>{
    if (mySeller?.resubmitRequested) {
      setSF({name:mySeller.name||"",handle:mySeller.handle||"",city:mySeller.city||"",bio:mySeller.bio||"",email:mySeller.email||session?.user?.email||"",instagram:mySeller.instagram||"",whatsapp:mySeller.whatsapp||""});
    }
  // eslint-disable-next-line
  },[mySeller?.resubmitRequested]);

  // Admin invite link — pre-fill form and switch to apply tab
  useEffect(()=>{
    if (!inviteParams?.adminInvited || !session) return;
    setSF(p=>({...p, name:inviteParams.name||p.name, city:inviteParams.city||p.city, whatsapp:inviteParams.phone||p.whatsapp }));
    switchTab("apply");
  // eslint-disable-next-line
  },[inviteParams?.adminInvited, !!session]);

  const openEnquiry = (listing) => {
    if (!session) { openAuth("buyer"); return; }
    const proceed = () => { setEL(listing); setEM(""); setED(null); };
    requireProfile ? requireProfile(proceed) : proceed();
  };

  const confirmEnquiry = async () => {
    const reqId = "ENQ-"+Date.now().toString(36).toUpperCase();
    const req = {
      id:reqId, type:"enquiry",
      watch:`${enquiryListing.brand} ${enquiryListing.model}`,
      listingId:enquiryListing.id, sellerId:enquiryListing.sellerId,
      userId:session.user.id, name:session.user.name, email:session.user.email, phone:session.user.phone||"",
      message:enquiryMsg, status:"Received", amount:0,
      createdAt:new Date().toISOString(),
      updates:[{date:new Date().toISOString(),note:"Buyer enquiry received."}]
    };
    saveRequests([...requests,req]);
    setED(reqId);
    toast("Enquiry sent — deal room opened.");
    // Auto-create a deal room for this enquiry
    try {
      await supabase.from("chat_rooms").upsert({
        id: "deal-" + reqId,
        type: "deal",
        name: `${enquiryListing.brand} ${enquiryListing.model} — Deal Room`,
        request_id: reqId,
        participants: [...new Set([session.user.id, users.find(u=>u.sellerId===enquiryListing.sellerId)?.id || enquiryListing.sellerId, ...(users||[]).filter(u=>(u.roles||[]).includes("admin")||u.role==="admin").map(u=>u.id)].filter(Boolean))],
        created_at: new Date().toISOString()
      }, { onConflict: "id", ignoreDuplicates: true });
    } catch {}
    if (addNotif) {
      const sellerObj = sellers.find(s=>s.id===enquiryListing.sellerId);
      const sellerUser = users.find(u=>u.sellerId===enquiryListing.sellerId);
      addNotif(sellerUser?.id||enquiryListing.sellerId, {
        type:"enquiry",
        title:`New enquiry — ${enquiryListing.brand} ${enquiryListing.model}`,
        body:`${session.user.name} is interested in your listing. A deal room has been opened in Chat.`,
        link:{view:"chat"},
        emailTo:sellerObj?.email,
        emailSubject:`New enquiry on your ${enquiryListing.brand} ${enquiryListing.model}`,
        whatsappTo:sellerObj?.whatsapp,
        whatsappTemplate:"thc_buyer_update",
        whatsappParams:[{name:"1",value:sellerObj?.name||"Seller"},{name:"2",value:`${enquiryListing.brand} ${enquiryListing.model}`},{name:"3",value:`New buyer enquiry (${reqId}). ${session.user.name} is interested — a deal room has been opened. Reply here or log in to chat.`}],
      });
    }
  };

  const [mktListingPhotos, setMLP]  = useState([]);
  const [mktListingUpl, setMLUpl]   = useState(false);

  const submitListing = async () => {
    if(!lf.brand||!lf.model||!lf.price){toast("Brand, model, price required.");return;}
    setMLUpl(true);
    const lid="l"+Date.now();
    let photoUrls=[];
    for(let i=0;i<mktListingPhotos.length;i++){
      try{
        const compressed=await compressImage(mktListingPhotos[i].file,1600,0.88);
        const path=`listings/${lid}/${i}.jpg`;
        const{error}=await supabase.storage.from("watch-photos").upload(path,compressed,{upsert:true,contentType:"image/jpeg"});
        if(!error){const{data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path);photoUrls.push(publicUrl);}
      }catch{}
    }
    setMLUpl(false);
    saveListings([...listings,{...lf,id:lid,sellerId:mySellerId||"pending",verified:false,photos:photoUrls,createdAt:new Date().toISOString(),price:parseInt(lf.price)}]);
    setLF({brand:"",model:"",reference:"",decade:"",condition:"",price:"",description:"",tags:""});
    setMLP([]);
    toast("Listing submitted for review."); switchTab("browse");
  };

  const validateSF = () => {
    const e = {};
    if (!sf.name.trim()) e.name = "Full name is required";
    if (!sf.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sf.email.trim())) e.email = "Enter a valid email address";
    if (sf.bio.trim().length < 20) e.bio = `Tell us a bit more (${Math.max(0,20-sf.bio.trim().length)} chars to go)`;
    if (sf.whatsapp.trim()) {
      const d = sf.whatsapp.replace(/\D/g,"");
      if (d.length < 7 || d.length > 15) e.whatsapp = "Enter a valid number (7–15 digits)";
    }
    setSFErrs(e);
    return Object.keys(e).length === 0;
  };

  const handleApplySuccess = async () => {
    if (!validateSF()) { toast("Please fix the errors below."); return; }
    if(!idFile && !mySeller?.resubmitRequested && !inviteParams?.adminInvited){toast("Please upload a government ID.");return;}
    setIdu(true);
    // Resubmission: reuse existing seller ID; new application: create new ID
    const sid = mySeller?.resubmitRequested ? mySeller.id : "s"+Date.now();
    let idDocUrl="";
    try {
      const compressed = await compressImage(idFile, 1600, 0.88);
      const path=`seller-ids/${sid}/id.jpg`;
      const {error}=await supabase.storage.from("watch-photos").upload(path,compressed,{contentType:"image/jpeg",upsert:true});
      if (!error) {
        const {data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path);
        idDocUrl=publicUrl;
      }
    } catch {}
    let avatarUrl="";
    try {
      const faceBlob = await cropPhotoFromId(idFile);
      if (faceBlob) {
        const facePath=`seller-ids/${sid}/avatar.jpg`;
        const {error:fe}=await supabase.storage.from("watch-photos").upload(facePath,faceBlob,{contentType:"image/jpeg",upsert:true});
        if (!fe) { const {data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(facePath); avatarUrl=publicUrl; }
      }
    } catch {}
    setIdu(false);
    const av=(sf.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2));
    const isResubmit = mySeller?.resubmitRequested;
    const existing = isResubmit ? mySeller : null;
    const newSellerObj = {
      ...(existing||{}), ...sf, id:sid, verified:isAdmin||!!inviteParams?.adminInvited,
      rating:existing?.rating||0, reviewCount:existing?.reviewCount||0,
      joinDate:existing?.joinDate||new Date().toISOString().slice(0,7),
      sales:existing?.sales||0, badge:inviteParams?.adminInvited?"Trusted":null, avatar:av,
      avatarUrl: avatarUrl||existing?.avatarUrl||"",
      idDocUrl: idDocUrl||existing?.idDocUrl||"",
      idVerified:isAdmin||!!inviteParams?.adminInvited, resubmitRequested:false, resubmitNote:"",
    };
    // Targeted upsert — does NOT touch verified/idVerified on existing rows
    const { error: selErr } = await supabase.from("sellers").upsert(newSellerObj, { onConflict: "id" });
    if (selErr) { toast("Failed to submit application — please try again."); setIdu(false); return; }
    saveSellers(isResubmit ? sellers.map(x=>x.id!==sid?x:newSellerObj) : [...sellers, newSellerObj]);
    if (session?.user) {
      const updatedUser = {...session.user, roles:[...new Set([...(session.user.roles||[session.user.role]),"seller"])], sellerId:sid};
      saveUsers(users.map(u=>u.id===session.user.id?updatedUser:u));
      refreshSession(updatedUser);
    }
    setAD(true); toast(inviteParams?.adminInvited?"You're live as a seller — welcome to THC.watch!":isResubmit?"Resubmission received — we'll review your updated details.":"Application received — we'll review your ID within 72 hours.");
    if (addNotif) addNotif("admin", {type:"seller", title:`${inviteParams?.adminInvited?"Invited seller joined":isResubmit?"Seller resubmission":"New seller application"} — ${sf.name}`, body:`${sf.name} (${sf.email}) ${inviteParams?.adminInvited?"joined via invite link":"has "+(isResubmit?"resubmitted":"applied to become")+" a seller"} on THC.watch.`, link:{view:"admin",tab:"sellers"}, emailTo:ADMIN_EMAIL, emailSubject:`${inviteParams?.adminInvited?"Invited seller joined":"New seller application"} from ${sf.name}`});
  };

  return (
    <div className="fade-in">
      {/* Enquiry modal — fixed overlay, appears over both browse and detail */}
      {enquiryListing && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setEL(null)}>
          <div style={{background:"#F0E8D8",borderRadius:16,padding:32,maxWidth:420,width:"100%",boxShadow:"0 24px 64px rgba(0,0,0,.25)"}} onClick={e=>e.stopPropagation()}>
            {enquiryDone ? (
              <div style={{textAlign:"center",padding:"8px 0"}}>
                <div style={{fontSize:44,color:"var(--grn)",marginBottom:12}}>✓</div>
                <h3 style={{fontSize:24,marginBottom:8}}>{t("mkt_enq_done_h",lang)}</h3>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",marginBottom:20}}>Ref: {enquiryDone}</div>
                <div style={{display:"flex",gap:10}}>
                  <button className="btn-ghost" style={{flex:1}} onClick={()=>setEL(null)}>Close</button>
                  <button className="btn-ink" style={{flex:1}} onClick={()=>{setEL(null);setView("track");}}>Track →</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.15em",marginBottom:6,textTransform:"uppercase"}}>{enquiryListing.brand} · {enquiryListing.decade}</div>
                <h3 style={{fontSize:24,marginBottom:16}}>{enquiryListing.model}</h3>
                <div style={{marginBottom:16}}>
                  <label className="fl">{t("message_seller",lang)}</label>
                  <textarea value={enquiryMsg} onChange={e=>setEM(e.target.value)} rows={3} placeholder="Questions about condition, provenance, original parts…" autoCapitalize="sentences"/>
                </div>
                <p style={{fontSize:11,color:"#8A7E74",lineHeight:1.6,marginBottom:20}}>Your enquiry goes to the seller via the platform. Continue the conversation in your deal room after sending.</p>
                <div style={{display:"flex",gap:10}}>
                  <button className="btn-ghost" style={{flex:1}} onClick={()=>setEL(null)}>{t("cancel",lang)}</button>
                  <button className="btn-ink" style={{flex:1}} onClick={confirmEnquiry}>{t("mkt_send_enq",lang)}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Listing detail — full page, replaces browse grid */}
      {detailListing && (
        <div className="fade-in">
          <button className="btn-ghost" style={{fontSize:12,marginBottom:24,display:"inline-flex",alignItems:"center",gap:6}} onClick={()=>setDL(null)}>← Back to Listings</button>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:"32px 48px",marginBottom:40}}>
            {/* Gallery */}
            <div>
              <div style={{aspectRatio:"1/1",background:"#F4F0EB",borderRadius:10,overflow:"hidden",marginBottom:10}}>
                {detailListing.photos?.[galleryIdx]
                  ? <img src={detailListing.photos[galleryIdx]} alt={detailListing.model} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontFamily:"'Playfair Display',serif",fontSize:80,opacity:.08}}>◷</span></div>
                }
              </div>
              {detailListing.photos?.length > 1 && (
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {detailListing.photos.map((p,i)=>(
                    <div key={i} onClick={()=>setGI(i)} style={{width:64,height:64,borderRadius:6,overflow:"hidden",cursor:"pointer",border:i===galleryIdx?"2px solid var(--ink)":"2px solid transparent",opacity:i===galleryIdx?1:0.55,transition:"opacity .15s"}}>
                      <img src={p} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Info */}
            <div>
              {detailListing.verified && <span className="tag tag-gold" style={{display:"inline-block",marginBottom:10,fontSize:9}}>✦ Verified Listing</span>}
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.15em",textTransform:"uppercase",marginBottom:6}}>
                {detailListing.brand}{detailListing.decade?" · "+detailListing.decade:""}
              </div>
              <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:32,fontWeight:400,lineHeight:1.1,marginBottom:detailListing.reference?4:8}}>{detailListing.model}</h1>
              {detailListing.reference && <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",marginBottom:12}}>Ref {detailListing.reference}</div>}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:30,color:"var(--ink)"}}>₹{detailListing.price?.toLocaleString("en-IN")}</div>
                {detailListing.condition && <span className="tag">{detailListing.condition}</span>}
              </div>
              <div style={{display:"flex",gap:8,marginBottom:28}}>
                <button className="btn-ink" style={{flex:1,padding:"13px",fontSize:13}} onClick={()=>openEnquiry(detailListing)}>Enquire →</button>
                {(()=>{ const sel=getBySeller(detailListing.sellerId); const wNum=sel?.whatsapp?.replace(/\D/g,""); if(!wNum) return null; const num=wNum.startsWith("91")?wNum:"91"+wNum; return (
                  <a href={`https://wa.me/${num}`} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"13px 18px",fontSize:12,border:"1.5px solid #25D366",borderRadius:8,color:"#25D366",textDecoration:"none",fontFamily:"'DM Mono',monospace",whiteSpace:"nowrap"}}>WhatsApp</a>
                ); })()}
              </div>
              <div style={{borderTop:"1px solid var(--bd)",paddingTop:16,marginBottom:20}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:12}}>Watch Details</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px 20px"}}>
                  {[["Brand",detailListing.brand],["Model",detailListing.model],detailListing.reference&&["Reference",detailListing.reference],detailListing.decade&&["Era",detailListing.decade],detailListing.condition&&["Condition",detailListing.condition]].filter(Boolean).map(([k,v])=>(
                    <div key={k}>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:2}}>{k}</div>
                      <div style={{fontSize:13,color:"var(--ink)",fontWeight:500}}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              {(()=>{ const sel=getBySeller(detailListing.sellerId); return sel ? (
                <div style={{borderTop:"1px solid var(--bd)",paddingTop:16}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:12}}>Seller</div>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    {sel.avatarUrl ? <img src={sel.avatarUrl} className="avatar" style={{width:44,height:44,objectFit:"cover",flexShrink:0}} alt={sel.avatar}/> : <div className="avatar" style={{width:44,height:44,fontSize:14}}>{sel.avatar}</div>}
                    <div style={{flex:1}}>
                      <div style={{fontSize:15,fontWeight:500,marginBottom:2}}>{sel.name}</div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>{[sel.city,sel.rating&&`★ ${sel.rating}`].filter(Boolean).join(" · ")}</div>
                    </div>
                    {sel.verified && <span className="vbadge">✦ Trusted</span>}
                  </div>
                </div>
              ) : null; })()}
            </div>
          </div>
          {detailListing.description && (
            <div style={{borderTop:"1px solid var(--bd)",paddingTop:24,marginBottom:32,maxWidth:640}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:12}}>About This Watch</div>
              <p style={{fontSize:14,color:"var(--ink2)",lineHeight:1.9,whiteSpace:"pre-wrap"}}>{detailListing.description}</p>
            </div>
          )}
          {(()=>{
            const related = shown.filter(x=>x.id!==detailListing.id && x.brand===detailListing.brand).slice(0,4);
            if (!related.length) return null;
            return (
              <div style={{borderTop:"1px solid var(--bd)",paddingTop:24}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:16}}>More {detailListing.brand} Listings</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12}}>
                  {related.map(r=>(
                    <div key={r.id} style={{cursor:"pointer"}} onClick={()=>openDetail(r)}>
                      <div style={{aspectRatio:"1/1",background:"#F4F0EB",borderRadius:8,overflow:"hidden",marginBottom:6}}>
                        {r.photos?.[0] ? <img src={r.photos[0]} alt={r.model} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : null}
                      </div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",marginBottom:2}}>{r.brand}</div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:14,lineHeight:1.2,marginBottom:3}}>{r.model}</div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,color:"var(--ink)"}}>₹{r.price?.toLocaleString("en-IN")}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {!detailListing && <>
      <div style={{marginBottom:32}}>
        <span className="sl">{t("mkt_head",lang)}</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:40,letterSpacing:"-0.025em",marginBottom:8}}>{t("mkt_sub",lang)}</h2>
        <p style={{color:"#4A423A",fontSize:14,maxWidth:520,lineHeight:1.7}}>Every seller reviewed before verification. Real ratings from real buyers.</p>
      </div>

      {/* Trust bar */}
      <div style={{display:"flex",gap:20,marginBottom:28,padding:"14px 20px",background:"#fff",border:"1.5px solid var(--bd)",borderRadius:6,flexWrap:"wrap"}}>
        {[{icon:"✦",l:`${sellers.filter(s=>s.verified).length} Trusted Sellers`,c:"#B8860B"},{icon:"★",l:`${(sellers.filter(s=>s.rating>0).reduce((a,s)=>a+s.rating,0)/Math.max(sellers.filter(s=>s.rating>0).length,1)).toFixed(1)} Avg Rating`,c:"#B8860B"},{icon:"◉",l:`${listings.filter(l=>l.verified).length} Verified Listings`,c:"#2E7D52"},{icon:"⊕",l:"Trusted = admin-verified seller",c:"#3730A3"}].map(item=>(
          <div key={item.l} style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{color:item.c,fontSize:14}}>{item.icon}</span>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#4A423A"}}>{item.l}</span>
          </div>
        ))}
      </div>

      <div className="thc-tabs" style={{marginBottom:24}}>
        {(()=>{ const hideApply=(mySeller&&!mySeller.resubmitRequested)||(session?.user?.role==="admin")||(session?.user?.roles||[]).includes("admin"); return [["browse",t("mkt_tab_sale",lang)],["sell",t("mkt_tab_list",lang)],["sellers",t("mkt_tab_sellers",lang)],(!hideApply&&["apply",t("mkt_tab_apply",lang)])].filter(Boolean); })().map(([tv,l])=>(
          <button key={tv} className={`stab ${tab===tv?"active":""}`} onClick={()=>{switchTab(tv);setSelSel(null);}}>{l.toUpperCase()}</button>
        ))}
      </div>

      {tab==="browse" && (
        <div>
          <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap",alignItems:"center"}}>
            {[["all",t("all_lbl",lang)],["verified",t("mkt_filter_trusted",lang)]].map(([f2,l])=>(
              <button key={f2} onClick={()=>setFilt(f2)} style={{background:filt===f2?"#1A1612":"#fff",color:filt===f2?"#F0E8D8":"#4A423A",border:"1.5px solid",borderColor:filt===f2?"#1A1612":"var(--bd)",padding:"7px 16px",fontSize:12,borderRadius:10}}>{l}</button>
            ))}
            <span style={{marginLeft:"auto",fontFamily:"'DM Mono',monospace",fontSize:11,color:"#8A7E74"}}>{shown.length} listings</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
            {shown.map(l=>{
              const sel=getBySeller(l.sellerId);
              return (
                <div key={l.id} className="card" style={{padding:0,overflow:"hidden",cursor:"pointer"}} onClick={()=>openDetail(l)}>
                  {/* Square photo — no overlay */}
                  <div style={{position:"relative",aspectRatio:"1/1",background:"#F4F0EB"}}>
                    {l.photos?.length>0
                      ? <img src={l.photos[0]} alt={l.model} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontFamily:"'Playfair Display',serif",fontSize:40,opacity:.15}}>◷</span></div>
                    }
                    {l.verified && <div style={{position:"absolute",top:8,left:8}}><span className="tag tag-gold" style={{fontSize:9,padding:"3px 7px"}}>✦</span></div>}
                    {l.condition && <div style={{position:"absolute",top:8,right:8}}><span className="tag" style={{fontSize:9,padding:"3px 7px"}}>{l.condition}</span></div>}
                  </div>
                  {/* Info below photo */}
                  <div style={{padding:"12px 14px 14px"}}>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:3}}>{l.brand}{l.decade?" · "+l.decade:""}</div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,marginBottom:3,lineHeight:1.2}}>{l.model}</div>
                    {l.reference && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:6}}>Ref {l.reference}</div>}
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"var(--ink)",marginBottom:8}}>₹{l.price?.toLocaleString("en-IN")}</div>
                    {sel && (
                      <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:10}}>
                        {sel.avatarUrl ? <img src={sel.avatarUrl} className="avatar" style={{width:16,height:16,objectFit:"cover",flexShrink:0}} alt={sel.avatar}/> : <div className="avatar" style={{width:16,height:16,fontSize:6}}>{sel.avatar}</div>}
                        <span style={{fontSize:11,color:"var(--ink2)"}}>{sel.name}</span>
                        {sel.verified && <span className="vbadge" style={{fontSize:7,padding:"1px 4px"}}>✦</span>}
                        {sel.rating > 0 && <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginLeft:"auto"}}>★ {sel.rating}</span>}
                      </div>
                    )}
                    <button className="btn-ghost" style={{width:"100%",fontSize:12,padding:"8px 12px"}} onClick={e=>{e.stopPropagation();openDetail(l);}}>View Details →</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab==="sellers" && !selSel && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(238px,1fr))",gap:14}}>
          {sellers.map(s=><div key={s.id} onClick={()=>setSelSel(s)}><SellerCard seller={s} onClick={()=>setSelSel(s)}/></div>)}
        </div>
      )}

      {tab==="sellers" && selSel && (
        <div className="fade-in">
          <button className="btn-ghost" style={{fontSize:12,marginBottom:20}} onClick={()=>setSelSel(null)}>← {t("mkt_all_sellers",lang)}</button>
          <div className="card" style={{marginBottom:20}}>
            <div style={{display:"flex",gap:16,alignItems:"flex-start",marginBottom:16}}>
              {selSel.avatarUrl ? <img src={selSel.avatarUrl} className="avatar" style={{width:52,height:52,objectFit:"cover",flexShrink:0}} alt={selSel.avatar}/> : <div className="avatar" style={{width:52,height:52,fontSize:16}}>{selSel.avatar}</div>}
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700}}>{selSel.name}</div>
                  {selSel.verified && <span className="vbadge" style={selSel.badge==="Top Seller"?{background:"#FFF0F5",borderColor:"#F4AACA",color:"#9B2C5C"}:{}}>✦ {selSel.badge||"Trusted"}</span>}
                </div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",marginBottom:8}}>{selSel.handle} · {selSel.city}</div>
                <p style={{fontSize:13.5,color:"#4A423A",lineHeight:1.7,maxWidth:500}}>{selSel.bio}</p>
              </div>
            </div>
            <div className="divider" style={{margin:"14px 0"}}/>
            <div style={{display:"flex",gap:28,flexWrap:"wrap"}}>
              {[["Rating",`★ ${selSel.rating||"—"} (${selSel.reviewCount})`],["Sales",selSel.sales+" watches"],["Since",selSel.joinDate],["Status",selSel.verified?"Trusted ✦":"Pending"]].map(([k,v])=>(
                <div key={k}><div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",textTransform:"uppercase",marginBottom:3}}>{k}</div><div style={{fontSize:14,fontWeight:500,color:k==="Status"&&selSel.verified?"#B8860B":"#1A1612"}}>{v}</div></div>
              ))}
            </div>
          </div>
          <span className="sl">Listings by {selSel.name}</span>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
            {listings.filter(l=>l.sellerId===selSel.id).map(l=>(
              <div key={l.id} className="card" style={{padding:0,overflow:"hidden",cursor:"pointer"}} onClick={()=>openDetail(l)}>
                <div style={{position:"relative",aspectRatio:"1/1",background:"#F4F0EB"}}>
                  {l.photos?.length>0
                    ? <img src={l.photos[0]} alt={l.model} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontFamily:"'Playfair Display',serif",fontSize:40,opacity:.15}}>◷</span></div>
                  }
                  {l.verified && <div style={{position:"absolute",top:8,left:8}}><span className="tag tag-gold" style={{fontSize:9,padding:"3px 7px"}}>✦</span></div>}
                  {l.condition && <div style={{position:"absolute",top:8,right:8}}><span className="tag" style={{fontSize:9,padding:"3px 7px"}}>{l.condition}</span></div>}
                </div>
                <div style={{padding:"12px 14px 14px"}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:3}}>{l.brand}{l.decade?" · "+l.decade:""}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,marginBottom:6,lineHeight:1.2}}>{l.model}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"var(--ink)",marginBottom:10}}>₹{l.price?.toLocaleString("en-IN")}</div>
                  <button className="btn-ghost" style={{width:"100%",fontSize:12,padding:"8px 12px"}} onClick={e=>{e.stopPropagation();openDetail(l);}}>View Details →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==="sell" && (
        <div style={{maxWidth:560}}>
          {!session ? (
            <div style={{textAlign:"center",padding:"48px 0"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,marginBottom:12}}>{t("mkt_signin_list",lang)}</div>
              <p style={{color:"#4A423A",marginBottom:24,lineHeight:1.7}}>You need a seller account to create listings.</p>
              <button className="btn-ink" onClick={()=>openAuth("seller")}>Sign in as Seller →</button>
            </div>
          ) : !isAdmin && ((mySeller && !mySeller.verified) || (!mySeller && isSeller)) ? (
            <div style={{textAlign:"center",padding:"48px 24px"}}>
              <div style={{fontSize:40,marginBottom:16}}>🕐</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,marginBottom:12}}>{t("mkt_review",lang)}</div>
              <p style={{color:"var(--ink2)",lineHeight:1.8,maxWidth:380,margin:"0 auto 20px"}}>Your seller application is being reviewed. You'll be able to list watches here once our team approves it — usually within 72 hours.</p>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)"}}>Questions? <a href="mailto:hello@thc.watch" style={{color:"var(--accent)"}}>hello@thc.watch</a></div>
            </div>
          ) : !mySeller ? (
            <div style={{textAlign:"center",padding:"48px 0"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,marginBottom:12}}>{t("mkt_apply_needed",lang)}</div>
              <p style={{color:"#4A423A",marginBottom:24,lineHeight:1.7}}>Apply to become a verified THC seller to create listings.</p>
              <button className="btn-ink" onClick={()=>switchTab("apply")}>{t("mkt_apply_sell",lang)}</button>
            </div>
          ) : (
            <>
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:"var(--gnbg)",border:"1px solid var(--gnbd)",borderRadius:6,marginBottom:20}}>
                {mySeller.avatarUrl ? <img src={mySeller.avatarUrl} className="avatar" style={{objectFit:"cover",flexShrink:0}} alt={mySeller.avatar}/> : <div className="avatar">{mySeller.avatar}</div>}
                <div><div style={{fontSize:14,fontWeight:500}}>{mySeller.name}</div><div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74"}}>✦ Trusted seller</div></div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                <div><label className="fl">{t("brand_lbl",lang)} *</label><select value={lf.brand} onChange={e=>lu("brand",e.target.value)}><option value="">Select</option><option>HMT</option><option>Allwyn</option><option>Ricoh</option></select></div>
                <div><label className="fl">{t("model_lbl",lang)} *</label><input value={lf.model} onChange={e=>lu("model",e.target.value)} placeholder={t("mkt_model_ph",lang)} type="text" autoComplete="off" autoCapitalize="words" required/></div>
                <div><label className="fl">{t("reference_lbl",lang)}</label><input value={lf.reference} onChange={e=>lu("reference",e.target.value)} placeholder="Ref no." type="text" autoComplete="off" autoCapitalize="characters" spellCheck={false}/></div>
                <div><label className="fl">{t("decade_lbl",lang)}</label><select value={lf.decade} onChange={e=>lu("decade",e.target.value)}><option value="">{t("any_lbl",lang)}</option>{["1960s","1970s","1980s","1990s"].map(d=><option key={d}>{d}</option>)}</select></div>
                <div><label className="fl">{t("condition_lbl",lang)}</label><select value={lf.condition} onChange={e=>lu("condition",e.target.value)}><option value="">Select</option>{["Mint","Excellent","Good","Fair"].map(c=><option key={c}>{c}</option>)}</select></div>
                <div><label className="fl">{t("price_lbl",lang)} *</label><input value={lf.price} onChange={e=>lu("price",e.target.value)} placeholder="2500" type="number" inputMode="numeric" min="0" autoComplete="off" required/></div>
                <div style={{gridColumn:"1/-1"}}><label className="fl">{t("desc_lbl",lang)} *</label><textarea value={lf.description} onChange={e=>lu("description",e.target.value)} rows={4} placeholder={t("mkt_desc_ph",lang)} autoCapitalize="sentences" required/></div>
              </div>
              <div style={{marginBottom:18}}>
                <label className="fl">{t("mkt_watch_photos",lang)}</label>
                <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:8}}>
                  {mktListingPhotos.map((p,i)=>(
                    <div key={i} style={{position:"relative"}}>
                      <img src={p.preview} alt="" style={{width:72,height:72,objectFit:"cover",borderRadius:8,border:"1px solid var(--bd)"}}/>
                      <button onClick={()=>setMLP(prev=>prev.filter((_,j)=>j!==i))} style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:9,background:"var(--red)",color:"#fff",border:"none",cursor:"pointer",fontSize:10,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                    </div>
                  ))}
                  {mktListingPhotos.length<6 && (
                    <label style={{width:72,height:72,border:"2px dashed var(--bd)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:22,color:"var(--ink3)"}}>
                      +
                      <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>{
                        const files=Array.from(e.target.files).slice(0,6-mktListingPhotos.length);
                        setMLP(prev=>[...prev,...files.map(f=>({file:f,preview:URL.createObjectURL(f)}))]);
                        e.target.value="";
                      }}/>
                    </label>
                  )}
                </div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>Up to 6 photos. First photo is the cover.</div>
              </div>
              <button className="btn-ink" onClick={submitListing} disabled={mktListingUpl} style={{opacity:mktListingUpl?0.6:1}}>{mktListingUpl?t("uploading_lbl",lang):t("mkt_submit_listing",lang)}</button>
            </>
          )}
        </div>
      )}

      {tab==="apply" && !appDone && !session && (
        <div style={{textAlign:"center",padding:"60px 0"}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,marginBottom:14}}>{t("mkt_signin_apply",lang)}</div>
          <p style={{color:"var(--ink2)",marginBottom:28,lineHeight:1.75,maxWidth:340,margin:"0 auto 28px"}}>You need an account before you can become a THC seller. It only takes a minute.</p>
          <button className="btn-ink" style={{padding:"10px 24px",fontSize:13}} onClick={()=>openAuth("seller")}>Sign in / Create account →</button>
        </div>
      )}
      {tab==="apply" && !appDone && !!session && (
        <div style={{maxWidth:580}}>
          {inviteParams?.adminInvited && (
            <div style={{background:"#F0F7F2",border:"1.5px solid #059669",borderRadius:8,padding:"14px 16px",marginBottom:20,display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{fontSize:22,lineHeight:1,marginTop:2}}>✦</div>
              <div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#065F46",letterSpacing:"0.12em",marginBottom:4}}>INVITED BY ADMIN</div>
                <div style={{fontSize:13,color:"#065F46",lineHeight:1.6}}>You've been personally invited to sell on THC.watch. No ID verification needed — just fill in your details and you're live.</div>
              </div>
            </div>
          )}
          {mySeller?.resubmitRequested && (
            <div style={{background:"#FFF8E7",border:"1.5px solid #F0D080",borderRadius:8,padding:"14px 16px",marginBottom:20}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#7A5C00",letterSpacing:"0.1em",marginBottom:6}}>NOTE FROM ADMIN</div>
              <div style={{fontSize:13,color:"#5C4400",lineHeight:1.7}}>{mySeller.resubmitNote||"Please resubmit your seller application with updated details."}</div>
            </div>
          )}
          {isSeller && !mySeller && (
            <div style={{background:"#FFF8E7",border:"1.5px solid #F0D080",borderRadius:8,padding:"12px 16px",marginBottom:20,fontSize:13,color:"#7A5C00"}}>
              Your previous seller application didn't save correctly. Please re-submit your details below — your account will be reviewed again.
            </div>
          )}
          <div style={{marginBottom:32,borderRadius:14,overflow:"hidden",border:"1px solid #2E2720"}}>
            <div style={{background:"#1A1612",padding:"28px 28px 24px",color:"#F0E8D8"}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.22em",color:"#B8860B",marginBottom:14,textTransform:"uppercase"}}>The Horological Catalog · Seller</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:30,marginBottom:8,lineHeight:1.2}}>Sell to people who actually care.</div>
              <p style={{fontSize:13,color:"#A89888",lineHeight:1.75,margin:0}}>THC.watch is India's curated marketplace for vintage HMT, Allwyn &amp; Ricoh watches. No fakes, no flippers — just serious transactions between verified people.</p>
            </div>
            <div style={{background:"#201C18",padding:"20px 28px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:2}}>
              {[
                ["◈","Zero listing fee","List for free. Pay nothing until you sell."],
                ["✦","Trusted seller badge","5 sales at 4.5★+ earns your Verified ✦ mark."],
                ["◎","Sourcing demand alerts","See buyers actively searching for your pieces."],
                ["⊡","Your own storefront","Public profile with ratings, bio & listing history."],
                ["◉","Direct buyer enquiries","Buyers message you — no intermediary cut."],
                ["⬡","Admin-backed disputes","We step in if something goes wrong — both sides."],
              ].map(([icon,title,desc])=>(
                <div key={title} style={{display:"flex",gap:10,padding:"14px 12px",borderTop:"1px solid rgba(255,255,255,0.04)"}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:18,color:"#B8860B",flexShrink:0,lineHeight:1,marginTop:2}}>{icon}</div>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:"#D4C9BB",marginBottom:3}}>{title}</div>
                    <div style={{fontSize:11,color:"#6A6059",lineHeight:1.55}}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{background:"#1A1612",padding:"14px 28px",borderTop:"1px solid #2E2720",display:"flex",gap:24,flexWrap:"wrap"}}>
              {[["01","Apply in 2 min"],["02","Review within 72 hrs"],["03","List immediately"],["04","Badge after 5 sales"]].map(([n,t])=>(
                <div key={n} style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#B8860B",letterSpacing:"0.1em"}}>{n}</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#6A6059"}}>{t}</span>
                </div>
              ))}
            </div>
          </div>
          {(()=>{
            const err = (k) => sfErrors[k] ? <div style={{color:"var(--red)",fontSize:11,marginTop:3}}>{sfErrors[k]}</div> : null;
            const phoneDigits = sf.whatsapp.replace(/\D/g,"");
            const phoneOk = phoneDigits.length >= 7 && phoneDigits.length <= 15;
            const handleSuggestion = sf.name && !sf.handle
              ? sf.name.trim().split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g,"") + "_watches"
              : null;
            return (
              <>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                <div>
                  <label className="fl">{t("name_lbl",lang)} *</label>
                  <input value={sf.name} onChange={e=>{su("name",e.target.value);if(sfErrors.name)setSFErrs(p=>({...p,name:""}));}} placeholder="Arjun Sharma" type="text" autoComplete="name" autoCapitalize="words" style={sfErrors.name?{borderColor:"var(--red)"}:{}}/>
                  {err("name")}
                </div>
                <div>
                  <label className="fl">{t("email_lbl",lang)} *</label>
                  <input value={sf.email} onChange={e=>{su("email",e.target.value);if(sfErrors.email)setSFErrs(p=>({...p,email:""}));}} placeholder="arjun@email.com" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} style={sfErrors.email?{borderColor:"var(--red)"}:{}}/>
                  {err("email")}
                </div>
                <div>
                  <label className="fl">{t("mkt_handle_lbl",lang)} <span style={{color:"#8A7E74",fontWeight:400}}>{t("optional_lbl",lang)}</span></label>
                  <input value={sf.handle} onChange={e=>su("handle",e.target.value.replace(/[^a-z0-9_]/gi,"").toLowerCase())} placeholder={handleSuggestion||"arjun_watches"} type="text" autoComplete="off" autoCapitalize="none" spellCheck={false}/>
                  {handleSuggestion && !sf.handle && <div style={{fontSize:10,color:"var(--ink3)",marginTop:3}}>Suggested: <button type="button" style={{background:"none",border:"none",color:"var(--accent)",fontSize:10,cursor:"pointer",padding:0}} onClick={()=>su("handle",handleSuggestion)}>{handleSuggestion}</button></div>}
                </div>
                <div>
                  <label className="fl">{t("city_lbl",lang)} <span style={{color:"#8A7E74",fontWeight:400}}>{t("optional_lbl",lang)}</span></label>
                  <input value={sf.city} onChange={e=>su("city",e.target.value)} placeholder="Mumbai" type="text" autoComplete="address-level2" autoCapitalize="words"/>
                  {!sf.city && <div style={{fontSize:10,color:"var(--ink3)",marginTop:3}}>Auto-filled when you upload your ID</div>}
                </div>
                <div style={{gridColumn:"1 / -1"}}>
                  <label className="fl">{t("phone_lbl",lang)} <span style={{color:"#8A7E74",fontWeight:400}}>(platform notifications only — not shared with buyers)</span></label>
                  <div style={{position:"relative"}}>
                    <input value={sf.whatsapp} onChange={e=>{su("whatsapp",e.target.value);if(sfErrors.whatsapp)setSFErrs(p=>({...p,whatsapp:""}));}} placeholder="+1 555 000 0000  or  9876543210" type="tel" inputMode="tel" autoComplete="tel" style={{...(sfErrors.whatsapp?{borderColor:"var(--red)"}:{}),paddingRight:32}}/>
                    {sf.whatsapp && <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:13}}>{phoneOk?"✓":"✗"}</span>}
                  </div>
                  {err("whatsapp")}
                  {!sfErrors.whatsapp && sf.whatsapp && phoneOk && <div style={{fontSize:10,color:"var(--grn)",marginTop:3}}>Looks good ✓</div>}
                  {!sf.whatsapp && <div style={{fontSize:10,color:"var(--ink3)",marginTop:3}}>Used to send you platform notifications (order updates, new enquiries)</div>}
                </div>
              </div>
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
                  <label className="fl" style={{margin:0}}>{t("mkt_bio_lbl",lang)} *</label>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:sf.bio.length>=20?"var(--grn)":"var(--ink3)"}}>{sf.bio.length} / 20 min</span>
                </div>
                <textarea value={sf.bio} onChange={e=>{su("bio",e.target.value);if(sfErrors.bio&&e.target.value.trim().length>=20)setSFErrs(p=>({...p,bio:""}));}} rows={3} placeholder="How long have you been collecting? What do you specialise in — HMT Pilot dials, Allwyn quartz, Ricoh automatics?" autoCapitalize="sentences" style={sfErrors.bio?{borderColor:"var(--red)"}:{}}/>
                {err("bio")}
              </div>
              </>
            );
          })()}
          {!inviteParams?.adminInvited && <div style={{marginBottom:20}}>
            <label className="fl">{t("mkt_gov_id_lbl",lang)} * <span style={{color:"#8A7E74",fontWeight:400}}>(Aadhaar, PAN, Driving Licence)</span></label>
            <div onClick={()=>document.getElementById("seller-id-input").click()}
              style={{border:"2px dashed var(--bd)",borderRadius:10,padding:"20px 24px",textAlign:"center",cursor:"pointer",background:"rgba(255,255,255,.4)"}}>
              {idChecking ? (
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#B8860B",letterSpacing:"0.1em"}}>AI CHECKING ID…</div>
              ) : idFile ? (
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
                  <img src={URL.createObjectURL(idFile)} alt="ID" style={{height:48,borderRadius:6,objectFit:"cover"}}/>
                  <div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:idValid?"#2E7D52":"#B8860B"}}>{idValid?"✓ ID auto-verified":"⚑ Team will review"}</div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>click to change</div>
                  </div>
                </div>
              ) : (
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#8A7E74"}}>CLICK TO UPLOAD ID PHOTO</div>
              )}
            </div>
            <input id="seller-id-input" type="file" accept="image/*" style={{display:"none"}} onChange={async e=>{
              const f=e.target.files[0]; if(!f) return;
              setIdFile(f); setIdValid(false); setIdChk(true);
              const [validResult, details] = await Promise.all([validateGovId(f), extractIdDetails(f)]);
              setIdChk(false);
              if (validResult?.isValidId) { setIdValid(true); toast("ID looks valid."); }
              else { setIdValid(false); toast("Couldn't auto-verify — our team will review your ID manually."); }
              if (details?.name) {
                setSF(p => ({
                  ...p,
                  name: p.name || details.name,
                  city: p.city || details.city || "",
                }));
                if (details.name) toast("Name auto-filled from ID.");
              }
            }}/>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginTop:6}}>Reviewed manually by our team. Not shared with buyers.</div>
          </div>}
          <button className="btn-ink" onClick={handleApplySuccess} disabled={idUploading||idChecking} style={{opacity:(idUploading||idChecking)?0.6:1}}>
            {idUploading?t("uploading_lbl",lang):idChecking?t("mkt_checking_id",lang):t("mkt_submit_app",lang)}
          </button>
          {Object.keys(sfErrors).length>0 && <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--red)",marginTop:10}}>Fix the errors above before submitting.</div>}
        </div>
      )}

      {tab==="apply" && appDone && (
        <div style={{textAlign:"center",padding:"60px 0"}}>
          <div style={{fontSize:48,color:"#2E7D52",fontWeight:400,marginBottom:12}}>✦</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:36,marginBottom:8}}>{inviteParams?.adminInvited?"You're live!":t("mkt_app_done_h",lang)}</div>
          <p style={{color:"#4A423A",fontSize:14,maxWidth:420,margin:"0 auto",lineHeight:1.75}}>{inviteParams?.adminInvited?"Your seller profile is active. Head to your dashboard to add listings and start selling.":"We review within 72 hours. Complete 5 sales at 4.5+ to earn your Verified badge."}</p>
          {inviteParams?.adminInvited && <button className="btn-ink" style={{marginTop:24,padding:"10px 24px",fontSize:13}} onClick={()=>setView("seller-dash")}>Go to Seller Dashboard →</button>}
        </div>
      )}
      </>}
    </div>
  );
}

// ─── SELLER DASHBOARD ─────────────────────────────────────────────────────────
// ─── Multi-offer sourcing ─────────────────────────────────────────────────────
// Sellers post one or more priced offers per request. Buyer accepts one (or many
// if they're a collector). Platform takes ₹500 flat from each seller's quote.
